import React from 'react';

import { Button, Stack } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { EmployeeSearchInputKindEnum } from 'src/api';
import { TextField } from 'src/components/finalForm';
import type { EmployeeSearchCriteria } from 'src/types/employeeSearch';

import { PhoneSearchFormFields } from './constants';
import type { PhoneSearchFormValues } from './types';
import { phoneSearchFormValidationSchema } from './validation';

type PhoneSearchFormProps = {
  onSubmit: (criteria: EmployeeSearchCriteria) => void;
  loading?: boolean;
  /** Search restored from the URL; prefills the form when it's a phone search. */
  initialCriteria?: EmployeeSearchCriteria | null;
};

export const PhoneSearchForm: React.FC<PhoneSearchFormProps> = ({
  onSubmit,
  loading,
  initialCriteria,
}) => {
  const handleSubmit = React.useCallback(
    (values: PhoneSearchFormValues) =>
      onSubmit({
        kind: EmployeeSearchInputKindEnum.Phone,
        phoneNumber: values.phoneNumber?.trim() || undefined,
      }),
    [onSubmit],
  );

  // Held in state so the reference stays stable — `Form` reinitializes on a new one.
  const [initialValues] = React.useState<Partial<PhoneSearchFormValues>>(() =>
    initialCriteria?.kind === EmployeeSearchInputKindEnum.Phone
      ? { [PhoneSearchFormFields.phoneNumber]: initialCriteria.phoneNumber ?? undefined }
      : PHONE_INITIAL_VALUES,
  );

  return (
    <Form<PhoneSearchFormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={phoneSearchFormValidationSchema}
      subscription={{ submitting: true, valid: true, dirty: true }}
      containerProps={{ mt: 0 }}
    >
      {({ submitting, valid, dirty }) => (
        <React.Fragment>
          <Field
            name={PhoneSearchFormFields.phoneNumber}
            component={TextField}
            label="Phone number"
            placeholder="(555) 123-4567"
            type="tel"
            disabled={submitting || loading}
            fullWidth
          />

          <Stack direction="row" justifyContent="flex-end" mt={3}>
            <Button
              type="submit"
              variant="contained"
              disabled={!valid || submitting || loading || !dirty}
              loading={submitting || loading}
            >
              Run search
            </Button>
          </Stack>
        </React.Fragment>
      )}
    </Form>
  );
};

const PHONE_INITIAL_VALUES: Partial<PhoneSearchFormValues> = {
  [PhoneSearchFormFields.phoneNumber]: undefined,
};
